import { useRef, useEffect, useCallback } from "react";
import { Renderer, Camera, Transform, Plane, Mesh, Program } from "ogl";

export const tabVertexShader = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

export const tabFragmentShader = `
  precision highp float;

  uniform vec2 uMouse;
  uniform float uHover;
  uniform float uTime;

  varying vec2 vUv;

  void main() {
    float t = uTime * 0.08;

    vec2 mouse = clamp(uMouse, vec2(0.0), vec2(1.0));
    float spotDist = length(vUv - mouse);
    float spot = exp(-spotDist * spotDist * 30.0) * uHover;

    float sweepAxis = dot(vUv, normalize(vec2(1.0, -0.45))) + mouse.x * 0.06 + t * 0.03;
    float sweep = smoothstep(0.47, 0.5, sweepAxis) * smoothstep(0.54, 0.51, sweepAxis) * uHover;

    float topRim = smoothstep(0.48, 0.0, vUv.y) * 0.35;

    float glow = spot * 0.22 + sweep * 0.16 + topRim * 0.12;
    gl_FragColor = vec4(1.0, 1.0, 1.0, glow);
  }
`;

export function prefersReducedMotion() {
  if (typeof window === "undefined") return true;
  const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  if (!query) return false;
  return query.matches;
}

export function useFeatureTabShader({ enabled, pillRef, canResize }) {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const programRef = useRef(null);
  const rafRef = useRef(null);
  const mouseRef = useRef([0.5, 0.5]);
  const hoverRef = useRef(0);
  const canResizeRef = useRef(canResize);

  const syncCanvasSize = useCallback(() => {
    if (!canResizeRef.current) return;
    const pill = pillRef.current;
    const renderer = rendererRef.current;
    if (!pill || !renderer) return;

    const { width, height } = pill.getBoundingClientRect();
    if (width < 1 || height < 1) return;

    renderer.setSize(width, height);
  }, [pillRef]);

  useEffect(() => {
    canResizeRef.current = canResize;
    if (canResize) {
      syncCanvasSize();
    }
  }, [canResize, syncCanvasSize]);

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) return undefined;
    if (!pillRef.current || !canvasRef.current) return undefined;

    let renderer;
    try {
      renderer = new Renderer({
        canvas: canvasRef.current,
        alpha: true,
        antialias: true,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
      });
    } catch {
      return undefined;
    }
    rendererRef.current = renderer;
    renderer.gl.clearColor(0, 0, 0, 0);

    const camera = new Camera(renderer.gl, {
      left: -1,
      right: 1,
      top: 1,
      bottom: -1,
      near: 0.1,
      far: 10,
    });
    camera.position.z = 1;

    const scene = new Transform();
    const geometry = new Plane(renderer.gl, { width: 2, height: 2 });

    const program = new Program(renderer.gl, {
      vertex: tabVertexShader,
      fragment: tabFragmentShader,
      uniforms: {
        uMouse: { value: [0.5, 0.5] },
        uHover: { value: 0 },
        uTime: { value: 0 },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    programRef.current = program;

    const mesh = new Mesh(renderer.gl, { geometry, program });
    mesh.setParent(scene);

    syncCanvasSize();

    const tick = (time) => {
      rafRef.current = requestAnimationFrame(tick);
      program.uniforms.uTime.value = time * 0.001;
      program.uniforms.uMouse.value[0] +=
        (mouseRef.current[0] - program.uniforms.uMouse.value[0]) * 0.18;
      program.uniforms.uMouse.value[1] +=
        (mouseRef.current[1] - program.uniforms.uMouse.value[1]) * 0.18;
      program.uniforms.uHover.value += (hoverRef.current - program.uniforms.uHover.value) * 0.15;
      renderer.render({ scene, camera });
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rendererRef.current = null;
      programRef.current = null;
    };
  }, [enabled, pillRef, syncCanvasSize]);

  const updatePointer = useCallback(
    (clientX, clientY) => {
      const pill = pillRef.current;
      if (!pill) return;

      const rect = pill.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;

      const x = (clientX - rect.left) / rect.width;
      const y = 1 - (clientY - rect.top) / rect.height;
      mouseRef.current = [
        Math.max(0, Math.min(1, x)),
        Math.max(0, Math.min(1, y)),
      ];
      hoverRef.current = 1;
    },
    [pillRef]
  );

  const handlePointerLeave = useCallback(() => {
    hoverRef.current = 0;
    mouseRef.current = [0.5, 0.5];
  }, []);

  return {
    canvasRef,
    updatePointer,
    handlePointerLeave,
    syncCanvasSize,
    active: enabled && !prefersReducedMotion(),
  };
}
